import { Component } from 'react';
import PropTypes from 'prop-types';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK } from './constants';

const CONNECTED_STATUS = 'connected';

export default class FacebookLoginComponent extends Component {
    static propTypes = {
        appId: PropTypes.string.isRequired,
        version: PropTypes.string,
        cookie: PropTypes.bool,
        language: PropTypes.string,
        fields: PropTypes.string,
        handleOnResponse: PropTypes.func.isRequired,
        scope: PropTypes.string,
        autoLogin: PropTypes.bool,
        xfbml: PropTypes.bool,
        renderProps: PropTypes.func.isRequired,
        handleOnLoadComplete: PropTypes.func,
        handleOnComponentUpdated: PropTypes.func,
        isLoggedIn: PropTypes.func,
        handleOnNotConnected: PropTypes.func,
        reRequestPermissions: PropTypes.bool,
        onSocialConnectVerified: PropTypes.func,
        isCheckoutAuthenticationFlow: PropTypes.bool,
        handleOnConnected: PropTypes.func,
        notLoggedIn: PropTypes.bool,
    };

    static defaultProps = {
        version: 'v2.10',
        cookie: false,
        language: 'en_US',
        fields: 'name,picture,first_name',
        scope: 'public_profile,email',
        autoLogin: true,
        xfbml: false,
        reRequestPermissions: false,
    };

    componentDidMount() {
        const { skipScriptLoading } = this.props;

        if (!skipScriptLoading) {
            this._loadSDK();
        }
    }

    componentDidUpdate() {
        const { handleOnComponentUpdated } = this.props;
        if (handleOnComponentUpdated) {
            handleOnComponentUpdated();
        }
    }

    state = {
        authResponse: null,
        isLoggedInUser: false,
        me: null,
        onLoad: true,
    };

    /**
     * Init FB object and if autoLogin parameter is true check the login status
     */
    /* istanbul ignore next */
    _fbInit = (forceLogin = false) => {
        const { appId, version, cookie, xfbml, autoLogin } = this.props;
        this.setState({ onLoad: true });

        if (appId) {
            // when forceLogin is true, force getting user information since sdk is already loaded
            if (forceLogin && autoLogin) {
                window?.FB?.getLoginStatus?.(this._checkLoginStatus);
            } else {
                window.fbAsyncInit = () => {
                    window?.FB?.init({
                        appId,
                        cookie,
                        xfbml,
                        version,
                    });

                    if (autoLogin) {
                        window?.FB?.getLoginStatus?.(this._checkLoginStatus);
                    }
                };
            }
        }
    };

    /**
     * Set Up the Facebook SDK for Javascript
     */
    _loadSDK = () => {
        const { language } = this.props;

        ((doc, tagName, id, fbInit) => {
            const element = doc.getElementsByTagName(tagName)[0];

            if (element) {
                if (doc.getElementById(id)) {
                    // if the sdk is already loaded, call fbInit anyway and force it to get user information
                    if (fbInit) {
                        fbInit(true);
                    }
                    return;
                }
                const js = doc.createElement(tagName);
                js.id = id;
                js.src = `https://connect.facebook.net/${language}/sdk.js#version=v2.0`;
                element.parentNode.insertBefore(js, element);

                /**
                 * It is possible that the SDK is still not loaded when calling this function
                 * but there is an window.fbAsyncInit function that will execute once the
                 * sdk has finished loading.
                 */
                if (fbInit) {
                    fbInit();
                }
            }
        })(document, 'script', 'facebook-jssdk', this._fbInit);
    };

    /**
     * Check login status and return the user information to the called method
     */
    _checkLoginStatus = ({ status, authResponse }) => {
        const {
            language,
            fields,
            handleOnResponse,
            handleOnLoadComplete,
            handleOnNotConnected,
            onSocialLogInUserAuthorized,
        } = this.props;

        if (status === CONNECTED_STATUS && authResponse) {
            if (window && window.FB) {
                window.FB.api(
                    '/me',
                    {
                        locale: language,
                        fields,
                    },
                    (me) => {
                        //TODO: This information could be useful to other parts of the package
                        // consider moving this to a shared store
                        this.setState({
                            me,
                            authResponse,
                            isLoggedInUser: true,
                        });

                        handleOnResponse({
                            ...this.state.me,
                            ...this.state.authResponse,
                        });
                    },
                );
                // This function should be fired when the user clicks on the facebook button, 'onLoad' prevents it from firing when the page is rendering
                if (onSocialLogInUserAuthorized && !this.state.onLoad) {
                    onSocialLogInUserAuthorized?.(
                        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
                        status,
                    );
                }
            }
        } else if (handleOnNotConnected) {
            handleOnNotConnected(status);
        }
        if (handleOnLoadComplete) {
            handleOnLoadComplete();
        }
    };

    /**
     * Login on Facebook
     */
    _facebookLogin = () => {
        const { scope, reRequestPermissions } = this.props;

        const requestParams = { scope };

        if (reRequestPermissions) {
            requestParams['auth_type'] = 'rerequest';
        }

        if (window && window.FB) {
            this.setState({ onLoad: false });
            window.FB.login(this._checkLoginStatus, requestParams);
        }
    };

    _onSocialConnectVerified = () => {
        this.props.onSocialConnectVerified?.(
            SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
            {
                ...this.state.me,
                ...this.state.authResponse,
                sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
            },
        );
    };

    render() {
        // If the user is already has a connected status and the user is
        const skipExplicitLogin =
            this.state.isLoggedInUser &&
            this.props.isCheckoutAuthenticationFlow;
        const component = this.props.renderProps({
            onClick: skipExplicitLogin
                ? this._onSocialConnectVerified
                : this._facebookLogin,
        });

        return component;
    }
}
